import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useIsPro } from "@/hooks/useIsPro";
import { usePaywallModal } from "@/contexts/PaywallModalContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart3,
  DollarSign,
  TrendingUp,
  Package,
  Activity,
  FileDown,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  Play,
  Info,
} from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Slider } from "@/components/ui/slider";
import { useToast } from "@/hooks/use-toast";
import { ChartContainer } from "@/components/ui/chart";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Legend,
  ResponsiveContainer,
} from "recharts";

const CURRENCY = "K";

const PAYMENT_METHOD_COLORS = ["#16a34a", "#2563eb", "#9333ea", "#d97706", "#0d9488", "#64748b"];

function formatK(n: number): string {
  return `${CURRENCY} ${n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/** Compact format for chart axes to prevent wrapping and keep alignment */
function formatKAxis(v: number): string {
  if (v >= 1000000) return `K ${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `K ${(v / 1000).toFixed(0)}k`;
  return `K ${v}`;
}

/** Normalize AI insight text: ensure K is prefix not suffix (e.g. "139,302 K" -> "K 139,302") */
function normalizeKInInsight(s: string): string {
  return s.replace(/(\d[\d,.]*)\s*K\b/gi, (_, num) => `K ${num.trim()}`);
}

function formatPct(n: number): string {
  const s = n >= 0 ? `+${n.toFixed(1)}%` : `${n.toFixed(1)}%`;
  return s;
}

function getMonthOptions(): { value: string; label: string }[] {
  const opts: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    opts.push({
      value,
      label: d.toLocaleDateString("en-GB", { month: "long", year: "numeric" }),
    });
  }
  return opts;
}

export interface MonthlyBreakdownRow {
  month: string;
  label: string;
  revenue: number;
  cogs: number;
  grossProfit: number;
  operatingExpenses: number;
  taxOnRevenue: number;
  netProfit: number;
  marginPct: number;
}

export type ReportPeriodMonths = 1 | 3 | 6 | 12;

const PERIOD_OPTIONS: { value: ReportPeriodMonths; label: string }[] = [
  { value: 1, label: "Single month" },
  { value: 3, label: "Last 3 months" },
  { value: 6, label: "Last 6 months" },
  { value: 12, label: "Last 12 months" },
];

function formatChartDate(date: string, periodMonths: ReportPeriodMonths = 1): string {
  if (periodMonths > 1 && /^\d{4}-\d{2}$/.test(date)) {
    const [y, m] = date.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  }
  return new Date(date).toLocaleDateString("en-GB", { month: "short", day: "numeric" });
}

export interface MonthlyReportData {
  month: string;
  periodMonths?: ReportPeriodMonths;
  periodLabel?: string;
  monthlyBreakdown?: MonthlyBreakdownRow[];
  period: { start: string; end: string };
  compareToPrevious: boolean;
  previousMonth?: string;
  businessName?: string | null;
  kpis: {
    totalRevenue: number;
    netProfit: number;
    marginPct: number;
    inventoryTurnover: number;
    businessHealthScore: number;
    businessPhase: string;
  };
  financials: {
    revenue: number;
    cogs: number;
    grossProfit: number;
    operatingExpenses: number;
    taxOnRevenue: number;
    netProfit: number;
    marginPct: number;
    expensesByCategory?: Record<string, number>;
  };
  budget: {
    target: number;
    actual: number;
    variancePct: number;
  };
  executiveSummary: {
    revenueVsPrevMonth: { current: number; previous: number; changePct: number };
    topProducts: {
      name: string;
      revenue: number;
      cogs: number;
      grossProfit: number;
      unitsSold: number;
      momChangePct: number;
      momUnitsChangePct: number;
      profitPerUnit: number;
    }[];
    businessPhase: string;
  };
  inventory: {
    turnoverRate: number;
    deadStockCount: number;
    cashAtRisk: number;
    deadStock: { itemName: string; currentStock: number; cashAtRisk: number }[];
    categoryBreakdown: Record<string, number>;
    productRanking?: {
      name: string;
      revenue: number;
      grossProfit: number;
      profitPct: number;
      unitsSold: number;
      stockValue: number;
      turnover: number;
      profitContribution: number;
      label: string;
    }[];
    scatterData?: { name: string; profitPct: number; turnover: number; label: string }[];
    inventoryHealthScore?: number;
    percentDeadStock?: number;
    percentMoneyStuck?: number;
  };
  cashFlow: {
    operatingCashFlow: number;
    netCashPosition: number;
    monthEndCashBalance: number;
    monthlySpending?: number;
    monthsOfSafety?: number;
    cashTrend6Month?: { month: string; label: string; value: number }[];
    monthlyChangePercent?: number;
    cashStabilityScore?: number;
    moneySafetyLevel?: "Safe" | "Watch Closely" | "Danger";
    cashTrendDirection?: "Growing" | "Stable" | "Shrinking";
    predicted30Days?: number;
    predicted90Days?: number;
  };
  charts: {
    revenueSeries: { date: string; value: number }[];
    previousRevenueSeries: { date: string; value: number }[];
  };
  ai: {
    insights: string[];
    priority: string;
    focusArea: string;
  };
}

function CashFlowSection({
  data,
  formatK,
  formatPct,
}: {
  data: MonthlyReportData;
  formatK: (n: number) => string;
  formatPct: (n: number) => string;
}) {
  const cf = data.cashFlow;
  const monthsOfSafety = cf.monthsOfSafety ?? (cf.monthlySpending && cf.monthlySpending > 0 ? cf.monthEndCashBalance / cf.monthlySpending : 0);
  const displayMonthsOfSafety = Math.min(monthsOfSafety, 24);
  const moneySafetyLevel = cf.moneySafetyLevel ?? "Safe";
  const cashTrend = cf.cashTrendDirection ?? "Stable";
  const trend6Month = cf.cashTrend6Month ?? [];
  const pred30 = cf.predicted30Days ?? cf.monthEndCashBalance;
  const pred90 = cf.predicted90Days ?? cf.monthEndCashBalance;
  const [salesAdj, setSalesAdj] = useState(0);
  const [spendAdj, setSpendAdj] = useState(0);
  const monthlySpending = cf.monthlySpending ?? cf.operatingExpenses ?? 1;
  const avgMonthlyProfit = pred30 - cf.monthEndCashBalance;
  const adjMonthlyProfit = avgMonthlyProfit * (1 + salesAdj / 100) - monthlySpending * (spendAdj / 100);
  const adjPred30 = cf.monthEndCashBalance + adjMonthlyProfit;
  const adjPred90 = cf.monthEndCashBalance + adjMonthlyProfit * 3;
  const adjMonthlyBurn = monthlySpending * (1 + spendAdj / 100);
  const adjMonthsOfSafety = adjMonthlyBurn > 0 ? adjPred30 / adjMonthlyBurn : 0;

  const safetyColor = moneySafetyLevel === "Safe" ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/50" : moneySafetyLevel === "Watch Closely" ? "bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/50" : "bg-red-500/20 text-red-700 dark:text-red-400 border-red-500/50";
  const trendColor = cashTrend === "Growing" ? "text-emerald-600" : cashTrend === "Shrinking" ? "text-red-600" : "text-muted-foreground";

  return (
    <div className="grid lg:grid-cols-3 gap-3 sm:gap-4 min-w-0">
      <div className="lg:col-span-2 space-y-3 sm:space-y-4 min-w-0">
        <Card>
          <CardHeader>
            <CardTitle>Is my business safe?</CardTitle>
            <CardDescription>Cash buffer based on recent spending, not a long-term guarantee</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="text-lg font-semibold cursor-help">
                  If sales stopped, your current cash could cover about{" "}
                  <span className="text-primary">{displayMonthsOfSafety.toFixed(1)}</span> months of average spending.
                </p>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                Based on month-end cash divided by average spending over the last 3 months. We cap this at 24 months so it stays realistic for planning.
              </TooltipContent>
            </Tooltip>
            <div className="flex flex-wrap gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className={`${safetyColor} cursor-help`}>
                    Money Safety: {moneySafetyLevel}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  Danger = &lt;3 months runway or cash dropped &gt;15%. Watch Closely = 3–6 months. Safe = &gt;6 months.
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className={`${trendColor} cursor-help`}>
                    Trend: {cashTrend}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  Based on this month vs last month cash. Growing = cash up. Shrinking = cash down.
                </TooltipContent>
              </Tooltip>
            </div>
            {trend6Month.length > 0 && (
              <ChartContainer id="cash-trend" config={{ value: { label: "Cash", color: "hsl(217, 91%, 60%)" } }} className="h-[180px] sm:h-[240px] w-full min-w-0">
                <LineChart data={trend6Month} margin={{ left: 56, right: 24, top: 16, bottom: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis width={56} tick={{ fontSize: 12 }} tickFormatter={(v) => formatKAxis(v)} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
                  <RechartsTooltip formatter={(v: number) => [formatK(v), "Cash"]} contentStyle={{ borderRadius: 8, fontSize: 13 }} />
                  <Line type="monotone" dataKey="value" stroke="hsl(217, 91%, 60%)" strokeWidth={3} dot={{ r: 5 }} activeDot={{ r: 7 }} />
                </LineChart>
              </ChartContainer>
            )}
            <div className="space-y-3 font-mono text-sm">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex justify-between py-2 border-b cursor-help">
                    <span>Month-end Cash Balance</span>
                    <span>{formatK(cf.monthEndCashBalance)}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>Revenue minus expenses for the selected month.</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex justify-between py-2 border-b cursor-help">
                    <span>Avg Monthly Spending (last 3 mo)</span>
                    <span>{formatK(monthlySpending)}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>Average operating expenses over the last 3 months. Used to calculate months of safety.</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex justify-between py-2 border-b cursor-help">
                    <span>Month-to-month cash change</span>
                    <span>{formatPct(cf.monthlyChangePercent ?? 0)}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>Percentage change in cash from last month. Negative = cash decreased.</TooltipContent>
              </Tooltip>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Money Prediction</CardTitle>
            <CardDescription>Adjust sliders to see impact on cash (client-side only)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm">Increase/Decrease Sales ({salesAdj >= 0 ? "+" : ""}{salesAdj}%)</Label>
              <Slider value={[salesAdj]} onValueChange={([v]) => setSalesAdj(v)} min={-20} max={20} step={1} className="mt-1" />
            </div>
            <div>
              <Label className="text-sm">Increase/Decrease Spending ({spendAdj >= 0 ? "+" : ""}{spendAdj}%)</Label>
              <Slider value={[spendAdj]} onValueChange={([v]) => setSpendAdj(v)} min={-20} max={20} step={1} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="cursor-help">
                    <p className="text-xs text-muted-foreground">Money in 30 days</p>
                    <p className="text-lg font-bold">{formatK(adjPred30)}</p>
                  </div>
                </TooltipTrigger>
                <TooltipContent>Estimated cash position in 30 days based on your slider adjustments.</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="cursor-help">
                    <p className="text-xs text-muted-foreground">Money in 90 days</p>
                    <p className="text-lg font-bold">{formatK(adjPred90)}</p>
                  </div>
                </TooltipTrigger>
                <TooltipContent>Estimated cash position in 90 days based on your slider adjustments.</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="cursor-help">
                    <p className="text-xs text-muted-foreground">Months of safety (adjusted)</p>
                    <p className="text-lg font-bold">{adjMonthsOfSafety.toFixed(1)}</p>
                  </div>
                </TooltipTrigger>
                <TooltipContent>How many months you can survive at adjusted spending.</TooltipContent>
              </Tooltip>
            </div>
          </CardContent>
        </Card>
      </div>
      <div>
        <AIInsightPanel insights={data.ai.insights} priority={data.ai.priority} focusArea={data.ai.focusArea} />
      </div>
    </div>
  );
}

const LABEL_COLORS: Record<string, string> = {
  "Top Money Maker": "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/50",
  "High Volume Item": "bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-500/50",
  "Money Trap": "bg-red-500/20 text-red-700 dark:text-red-400 border-red-500/50",
  "Stable Item": "bg-slate-500/20 text-slate-700 dark:text-slate-400 border-slate-500/50",
};

function InventorySection({ data, formatK }: { data: MonthlyReportData; formatK: (n: number) => string }) {
  const inv = data.inventory;
  const productRanking = inv.productRanking ?? [];
  const scatterData = inv.scatterData ?? [];
  const healthScore = inv.inventoryHealthScore ?? 50;
  const healthColor = healthScore > 75 ? "bg-emerald-500/20 text-emerald-700 border-emerald-500/50" : healthScore >= 50 ? "bg-amber-500/20 text-amber-700 border-amber-500/50" : "bg-red-500/20 text-red-700 border-red-500/50";

  return (
    <div className="grid lg:grid-cols-3 gap-3 sm:gap-4 min-w-0">
      <div className="lg:col-span-2 space-y-3 sm:space-y-4 min-w-0">
        <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
          <Card>
            <CardHeader className="pb-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <CardTitle className="text-sm cursor-help">Inventory Health Score</CardTitle>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  0–100 score from turnover (good), profit (good), dead stock % (bad), money stuck in stock (bad). Green &gt;75, Yellow 50–75, Red &lt;50.
                </TooltipContent>
              </Tooltip>
              <CardDescription>0–100: turnover, profit, dead stock, money stuck</CardDescription>
            </CardHeader>
            <CardContent>
              <div className={`flex items-center justify-center w-24 h-24 rounded-full border-4 font-bold text-2xl ${healthColor}`}>
                {healthScore}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {inv.percentDeadStock?.toFixed(1) ?? 0}% dead stock · {inv.percentMoneyStuck?.toFixed(0) ?? 0}% money stuck
              </p>
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 gap-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Turnover Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{(inv.turnoverRate * 100).toFixed(1)}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Dead Stock</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-red-600">{inv.deadStockCount} items · {formatK(inv.cashAtRisk)}</p>
              </CardContent>
            </Card>
          </div>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Product Ranking</CardTitle>
            <CardDescription>Which products help vs trap your money</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto -mx-1 px-1 sm:mx-0 sm:px-0">
              <table className="w-full text-sm min-w-[520px]" style={{ tableLayout: "auto" }}>
                <thead>
                  <tr className="border-b-2 border-border">
                    <th className="text-left py-2 font-semibold">Product</th>
                    <th className="text-right py-2 font-semibold">Label</th>
                    <th className="text-right py-2 font-semibold">Revenue</th>
                    <th className="text-right py-2 font-semibold">Profit %</th>
                    <th className="text-right py-2 font-semibold">Turnover</th>
                    <th className="text-right py-2 font-semibold">Stock Value</th>
                  </tr>
                </thead>
                <tbody>
                  {productRanking.map((p, i) => (
                    <tr key={i} className="border-b border-border/50 last:border-0">
                      <td className="py-2 font-medium">{p.name}</td>
                      <td className="py-2 text-right">
                        <Badge variant="outline" className={`text-[10px] ${LABEL_COLORS[p.label] ?? ""}`}>
                          {p.label}
                        </Badge>
                      </td>
                      <td className="py-2 text-right tabular-nums">{formatK(p.revenue)}</td>
                      <td className="py-2 text-right tabular-nums">{p.profitPct.toFixed(1)}%</td>
                      <td className="py-2 text-right tabular-nums">{p.turnover.toFixed(2)}</td>
                      <td className="py-2 text-right tabular-nums">{formatK(p.stockValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        {productRanking.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Product Performance: Profit % & Turnover</CardTitle>
              <CardDescription>Each bar shows Profit %. Color = product type. Hover for turnover (how fast it sells).</CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer id="product-performance" config={{}} className="h-[200px] sm:h-[280px] w-full min-w-0">
                <BarChart
                  data={productRanking.map((p) => ({ ...p, profitPctSafe: Math.min(100, Math.max(0, p.profitPct)) }))}
                  layout="vertical"
                  margin={{ left: 8, right: 24, top: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={100} axisLine={false} tickLine={false} />
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.[0]) return null;
                      const p = payload[0].payload;
                      return (
                        <div className="rounded-lg border bg-background p-3 shadow-lg text-sm min-w-[180px]">
                          <p className="font-semibold text-base mb-2">{p.name}</p>
                          <p className="text-muted-foreground">Profit: <span className="text-foreground font-semibold">{p.profitPct.toFixed(1)}%</span></p>
                          <p className="text-muted-foreground">Turnover: <span className="text-foreground font-semibold">{p.turnover.toFixed(2)}</span> (how fast it sells)</p>
                          <Badge variant="outline" className={`text-[10px] mt-2 ${LABEL_COLORS[p.label] ?? ""}`}>{p.label}</Badge>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="profitPctSafe" name="Profit %" radius={[0, 6, 6, 0]} maxBarSize={32}>
                    {productRanking.map((entry, i) => (
                      <Cell key={i} fill={entry.label === "Top Money Maker" ? "hsl(142, 76%, 36%)" : entry.label === "Money Trap" ? "hsl(0, 72%, 51%)" : entry.label === "High Volume Item" ? "hsl(217, 91%, 60%)" : "hsl(215, 14%, 50%)"} />
                    ))}
                  </Bar>
                </BarChart>
              </ChartContainer>
              <div className="flex flex-wrap gap-4 mt-3 pt-3 border-t text-xs text-muted-foreground">
                <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 mr-1" />Top Money Maker</span>
                <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-500 mr-1" />High Volume Item</span>
                <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 mr-1" />Money Trap</span>
                <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-slate-500 mr-1" />Stable Item</span>
              </div>
            </CardContent>
          </Card>
        )}
        {inv.deadStock.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Dead Stock Items
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {inv.deadStock.map((d, i) => (
                  <div key={i} className="flex justify-between py-2 border-b border-border/50 text-sm">
                    <span>{d.itemName}</span>
                    <span>{d.currentStock} units · {formatK(d.cashAtRisk)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <div>
        <AIInsightPanel insights={data.ai.insights} priority={data.ai.priority} focusArea={data.ai.focusArea} />
      </div>
    </div>
  );
}

function AIInsightPanel({
  insights,
  priority,
  focusArea,
}: {
  insights: string[];
  priority: string;
  focusArea: string;
}) {
  const priorityColor =
    priority === "high"
      ? "text-red-600 dark:text-red-400"
      : priority === "medium"
        ? "text-amber-600 dark:text-amber-400"
        : "text-emerald-600 dark:text-emerald-400";

  const sanitizeMoneySafetyText = (text: string | undefined): string | undefined => {
    if (!text) return text;
    return text.replace(
      /survive for [0-9.,]+\s+months[^.]*\./gi,
      "Your cash buffer looks strong at the current spending level, but keep aiming for a 3–6 month safety cushion."
    );
  };

  const normalizedInsights = insights
    .map((s) => normalizeKInInsight(s))
    .map((s) => sanitizeMoneySafetyText(s) ?? s);

  return (
    <Card className="border-border/60 bg-muted/30 min-w-0 overflow-hidden">
      <CardHeader className="pb-2 min-w-0">
        <CardTitle className="text-sm font-medium break-words">BI Analyst Insights</CardTitle>
        <CardDescription className="break-words">Real-time data interpretation ✨</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 min-w-0">
        <div className="text-sm leading-relaxed text-foreground space-y-2 break-words">
          {normalizedInsights.map((s, i) => (
            <p key={i} className="m-0 break-words">{s}</p>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className={`${priorityColor} cursor-help`}>
                Priority: {priority}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>How urgent these insights are. High = act soon.</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary" className="cursor-help">
                Focus: {focusArea.replace(/_/g, " ")}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>Main area to focus on: revenue, margin, inventory, cash flow, or operations.</TooltipContent>
          </Tooltip>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ReportsPage() {
  const { businessId, user, role, refreshProfile, profile } = useAuth();
  const { toast } = useToast();
  const { openPaywall } = usePaywallModal();
  const isPro = useIsPro();
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defaultMonth);
  const [periodMonths, setPeriodMonths] = useState<ReportPeriodMonths>(1);
  const [compareToPrevious, setCompareToPrevious] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<MonthlyReportData | null>(null);
  const [paymentBreakdown, setPaymentBreakdown] = useState<{ name: string; value: number }[]>([]);

  const monthOptions = useMemo(() => getMonthOptions(), []);

  useEffect(() => {
    if (!data?.period || !businessId) return;
    const start = data.period.start;
    const end = data.period.end;
    const q = supabase
      .from("sales")
      .select("final_amount, total_price, quantity, unit_price, payment_method")
      .is("voided_at", null)
      .gte("created_at", start)
      .lte("created_at", end)
      .eq("business_id", businessId);
    q.then(({ data: salesRows }) => {
      const list = (salesRows ?? []) as { final_amount?: number | null; total_price?: number | null; quantity?: number | null; unit_price?: number | null; payment_method?: string | null }[];
      const byMethod: Record<string, number> = {};
      list.forEach((s) => {
        const amount = Number(s.final_amount ?? s.total_price ?? (s.quantity && s.unit_price ? s.quantity * s.unit_price : 0));
        const method = s.payment_method?.trim() || "Cash";
        byMethod[method] = (byMethod[method] ?? 0) + amount;
      });
      setPaymentBreakdown(
        Object.entries(byMethod).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value)
      );
    });
  }, [data?.period?.start, data?.period?.end, businessId]);
  const isMonth2Plus = monthOptions.length >= 2 && month !== monthOptions[0].value && month !== monthOptions[1].value;
  const comparePeriodLabel = periodMonths === 1 ? "prior month" : "prior period";

  const handlePeriodMonthsChange = (value: string) => {
    const next = Number(value) as ReportPeriodMonths;
    if (next > 1 && !isPro) {
      openPaywall("export_report");
      return;
    }
    setPeriodMonths(next);
  };

  const fetchReport = useCallback(async () => {
    if (!user) {
      toast({ title: "Please wait", description: "Session is loading. Try again in a moment.", variant: "destructive" });
      return;
    }
    if (!businessId) return;
    if (!isPro && isMonth2Plus) {
      openPaywall("history_limit");
      return;
    }
    if (!isPro && periodMonths > 1) {
      openPaywall("export_report");
      return;
    }
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      toast({ title: "Session expired", description: "Please refresh the page and sign in again.", variant: "destructive" });
      return;
    }
    setLoading(true);
    setData(null);
    try {
      const { data: res, error } = await supabase.functions.invoke("get-monthly-report", {
        body: { month, compareToPrevious, periodMonths },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) {
        const body = res as { error?: string; detail?: string } | null;
        const msg = body?.detail ?? body?.error ?? error.message ?? "Function error";
        toast({
          title: "Report failed",
          description: msg,
          variant: "destructive",
        });
        return;
      }
      if ((res as { error?: string })?.error) {
        const body = res as { error?: string; detail?: string };
        toast({
          title: "Report failed",
          description: body.detail ?? body.error ?? "Unknown error",
          variant: "destructive",
        });
        return;
      }
      const base = res as MonthlyReportData;
      setData({
        ...base,
        businessName: profile?.business_name ?? base.businessName ?? null,
      });
    } catch (err) {
      console.error("ReportsPage fetch:", err);
      toast({
        title: "Report failed",
        description: err instanceof Error ? err.message : "Network error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [user, businessId, month, compareToPrevious, periodMonths, toast, isPro, isMonth2Plus, openPaywall, profile?.business_name]);

  useEffect(() => {
    if (data) fetchReport();
  }, [month, compareToPrevious, periodMonths]);

  const handleExportPDF = useCallback(() => {
    if (!isPro) {
      openPaywall("export_report");
      return;
    }
    toast({
      title: "Generating report",
      description: "Executive PDF will open in a new tab.",
    });
    import("./ReportsPdfExport").then(({ exportMonthlyReportPdf }) => {
      if (data) exportMonthlyReportPdf(data);
      else toast({ title: "No data", description: "Load report first.", variant: "destructive" });
    }).catch(() => {
      toast({ title: "Export unavailable", description: "PDF export is loading.", variant: "destructive" });
    });
  }, [data, toast, isPro, openPaywall]);

  if (!businessId) {
    return (
      <div className="p-8 text-center space-y-4">
        <p className="text-muted-foreground">
          {role === "admin"
            ? "Setting up your account. If you just signed up, click Refresh to load your business."
            : "Reports require admin access."}
        </p>
        {role === "admin" && (
          <Button variant="outline" onClick={() => refreshProfile()}>
            Refresh
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 p-4 sm:p-6 max-w-7xl mx-auto min-w-0 w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 min-w-0">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight break-words">
            {data?.periodLabel ? `${data.periodLabel} Report` : "Monthly Report Center"}
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 break-words">
            {profile?.business_name
              ? `${profile.business_name} · Rolling P&L, cash flow, inventory, and executive summary.`
              : "Rolling P&L, cash flow, inventory, and executive summary. Contains the official Business Health Score. Use for planning and review."}
          </p>
        </div>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-2 min-w-0">
            <Label htmlFor="period-select" className="text-sm whitespace-nowrap shrink-0">Period</Label>
            <Select value={String(periodMonths)} onValueChange={handlePeriodMonthsChange}>
              <SelectTrigger id="period-select" className="w-full min-w-[140px] max-w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={String(o.value)}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <Label htmlFor="month-select" className="text-sm whitespace-nowrap shrink-0">End month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger id="month-select" className="w-full min-w-[140px] max-w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="compare"
              checked={compareToPrevious}
              onCheckedChange={setCompareToPrevious}
            />
            <Label htmlFor="compare" className="text-sm cursor-pointer">
              Compare to {comparePeriodLabel}
            </Label>
          </div>
          <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={!data}>
            <FileDown className="h-4 w-4 mr-2" />
            Download Executive Report
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="h-12 w-12 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading report…</p>
        </div>
      ) : data ? (
        <>
          {/* Executive KPI Header */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 min-w-0 w-full">
            <Card className="min-w-0">
              <CardHeader className="pb-2 min-w-0">
                <CardTitle className="text-sm font-medium flex items-center gap-1 min-w-0 flex-wrap">
                  <DollarSign className="h-4 w-4 shrink-0" />
                  <span>Total Revenue</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help shrink-0" />
                    </TooltipTrigger>
                    <TooltipContent>Total sales revenue for the selected period. Sum of all sale amounts.</TooltipContent>
                  </Tooltip>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{formatK(data.kpis.totalRevenue)}</p>
                {data.compareToPrevious && (
                  <p className="text-xs mt-1 flex items-center gap-0.5">
                    {data.executiveSummary.revenueVsPrevMonth.changePct >= 0 ? (
                      <ArrowUpRight className="h-3.5 w-3.5 text-emerald-600" />
                    ) : (
                      <ArrowDownRight className="h-3.5 w-3.5 text-red-600" />
                    )}
                    {formatPct(data.executiveSummary.revenueVsPrevMonth.changePct)} vs {comparePeriodLabel}
                  </p>
                )}
              </CardContent>
            </Card>
            <Card className="min-w-0">
              <CardHeader className="pb-2 min-w-0">
                <CardTitle className="text-sm font-medium flex items-center gap-1 min-w-0 flex-wrap">
                  <TrendingUp className="h-4 w-4 shrink-0" />
                  <span>Net Profit</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help shrink-0" />
                    </TooltipTrigger>
                    <TooltipContent>Revenue minus COGS minus Operating Expenses. Money left after all costs.</TooltipContent>
                  </Tooltip>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{formatK(data.kpis.netProfit)}</p>
                <p className="text-xs mt-1 text-muted-foreground">
                  Margin {data.kpis.marginPct.toFixed(1)}%
                </p>
              </CardContent>
            </Card>
            <Card className="min-w-0">
              <CardHeader className="pb-2 min-w-0">
                <CardTitle className="text-sm font-medium flex items-center gap-1 min-w-0 flex-wrap">
                  <BarChart3 className="h-4 w-4 shrink-0" />
                  <span>Margin %</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help shrink-0" />
                    </TooltipTrigger>
                    <TooltipContent>Net Profit ÷ Revenue × 100. Percentage of revenue kept as profit.</TooltipContent>
                  </Tooltip>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{data.kpis.marginPct.toFixed(1)}%</p>
              </CardContent>
            </Card>
            <Card className="min-w-0">
              <CardHeader className="pb-2 min-w-0">
                <CardTitle className="text-sm font-medium flex items-center gap-1 min-w-0 flex-wrap">
                  <Package className="h-4 w-4 shrink-0" />
                  <span>Inventory Turnover</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help shrink-0" />
                    </TooltipTrigger>
                    <TooltipContent>Units sold ÷ Total inventory units. Higher = stock moving faster.</TooltipContent>
                  </Tooltip>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{(data.kpis.inventoryTurnover * 100).toFixed(1)}%</p>
              </CardContent>
            </Card>
            <Card className="min-w-0">
              <CardHeader className="pb-2 min-w-0">
                <CardTitle className="text-sm font-medium flex items-center gap-1 min-w-0 flex-wrap">
                  <Activity className="h-4 w-4 shrink-0" />
                  <span>Business Health Score</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help shrink-0" />
                    </TooltipTrigger>
                    <TooltipContent>Official monthly composite score (velocity, margin, turnover, risk). Expansion = strong; Decline Risk = needs action. The 7-Day Snapshot in BI Analyst uses the same formula for quick trends.</TooltipContent>
                  </Tooltip>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{data.kpis.businessHealthScore}</p>
                <Badge variant="outline" className="mt-1">
                  {data.kpis.businessPhase}
                </Badge>
              </CardContent>
            </Card>
          </div>

          {(data.periodMonths ?? 1) > 1 && data.monthlyBreakdown && data.monthlyBreakdown.length > 0 && (
            <Card className="min-w-0 overflow-hidden">
              <CardHeader className="min-w-0">
                <CardTitle className="text-base break-words">Monthly Breakdown</CardTitle>
                <CardDescription className="break-words">
                  Month-by-month P&L for {data.periodLabel ?? "the selected period"}
                </CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead>
                    <tr className="border-b-2 border-border">
                      <th className="text-left py-3 font-semibold">Month</th>
                      <th className="text-right py-3 font-semibold">Revenue</th>
                      <th className="text-right py-3 font-semibold">COGS</th>
                      <th className="text-right py-3 font-semibold">Gross</th>
                      <th className="text-right py-3 font-semibold">OPEX</th>
                      <th className="text-right py-3 font-semibold">Tax</th>
                      <th className="text-right py-3 font-semibold">Net</th>
                      <th className="text-right py-3 font-semibold">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.monthlyBreakdown.map((row) => (
                      <tr key={row.month} className="border-b border-border/50 last:border-0">
                        <td className="py-2 font-medium">{row.label}</td>
                        <td className="py-2 text-right tabular-nums">{formatK(row.revenue)}</td>
                        <td className="py-2 text-right tabular-nums text-red-600">-{formatK(row.cogs)}</td>
                        <td className="py-2 text-right tabular-nums">{formatK(row.grossProfit)}</td>
                        <td className="py-2 text-right tabular-nums text-red-600">-{formatK(row.operatingExpenses)}</td>
                        <td className="py-2 text-right tabular-nums text-red-600">-{formatK(row.taxOnRevenue)}</td>
                        <td className={`py-2 text-right tabular-nums font-medium ${row.netProfit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {formatK(row.netProfit)}
                        </td>
                        <td className="py-2 text-right tabular-nums">{row.marginPct.toFixed(1)}%</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-border font-semibold bg-muted/30">
                      <td className="py-2.5">Total</td>
                      <td className="py-2.5 text-right tabular-nums">{formatK(data.financials.revenue)}</td>
                      <td className="py-2.5 text-right tabular-nums text-red-600">-{formatK(data.financials.cogs)}</td>
                      <td className="py-2.5 text-right tabular-nums">{formatK(data.financials.grossProfit)}</td>
                      <td className="py-2.5 text-right tabular-nums text-red-600">-{formatK(data.financials.operatingExpenses)}</td>
                      <td className="py-2.5 text-right tabular-nums text-red-600">-{formatK(data.financials.taxOnRevenue)}</td>
                      <td className={`py-2.5 text-right tabular-nums ${data.financials.netProfit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {formatK(data.financials.netProfit)}
                      </td>
                      <td className="py-2.5 text-right tabular-nums">{data.financials.marginPct.toFixed(1)}%</td>
                    </tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Payment Method Breakdown: legend matches slices by explicit payload */}
          {paymentBreakdown.length > 0 && (
            <Card className="min-w-0 overflow-hidden">
              <CardHeader className="min-w-0">
                <CardTitle className="text-base break-words">Payment Method Breakdown</CardTitle>
                <CardDescription className="break-words">Revenue by payment method for the selected period</CardDescription>
              </CardHeader>
              <CardContent className="overflow-hidden">
                <div className="h-[240px] sm:h-[260px] w-full min-w-0 overflow-hidden flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                      <Pie
                        data={paymentBreakdown}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={64}
                        startAngle={-90}
                        endAngle={270}
                        label={false}
                      >
                        {paymentBreakdown.map((entry, i) => (
                          <Cell key={entry.name} fill={PAYMENT_METHOD_COLORS[i % PAYMENT_METHOD_COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.[0]) return null;
                          const p = payload[0].payload as { name: string; value: number };
                          const total = paymentBreakdown.reduce((s, e) => s + e.value, 0);
                          const pct = total ? ((p.value / total) * 100).toFixed(0) : "0";
                          return (
                            <div className="rounded-lg border bg-background px-3 py-2 shadow-lg text-sm">
                              <p className="font-semibold text-foreground">{p.name}</p>
                              <p className="text-muted-foreground">{formatK(p.value)} ({pct}%)</p>
                            </div>
                          );
                        }}
                      />
                      <Legend
                        layout="vertical"
                        align="center"
                        verticalAlign="bottom"
                        payload={paymentBreakdown.map((entry, i) => {
                          const total = paymentBreakdown.reduce((s, e) => s + e.value, 0);
                          const pct = total ? ((entry.value / total) * 100).toFixed(0) : "0";
                          return {
                            value: `${entry.name} (${pct}%)`,
                            type: "square",
                            color: PAYMENT_METHOD_COLORS[i % PAYMENT_METHOD_COLORS.length],
                          };
                        })}
                        wrapperStyle={{ paddingTop: 8 }}
                        formatter={() => null}
                        content={({ payload }) => (
                          <ul className="flex flex-wrap justify-center gap-x-4 gap-y-2 list-none text-xs mt-2 p-0 m-0">
                            {payload?.map((entry, i) => (
                              <li key={i} className="flex items-center gap-2 shrink-0">
                                <span
                                  className="shrink-0 w-3.5 h-3.5 rounded-sm border border-border/50"
                                  style={{ backgroundColor: entry.color }}
                                  aria-hidden
                                />
                                <span className="text-foreground font-medium">{entry.value}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tabbed Content: scrollable tabs on mobile */}
          <Tabs defaultValue="executive" className="space-y-4 min-w-0">
            <TabsList className="flex w-full max-w-2xl overflow-x-auto gap-1 pb-1 -mx-1 px-1 scrollbar-thin shrink-0 flex-nowrap md:grid md:grid-cols-4 md:overflow-visible md:mx-0 md:px-0">
              <TabsTrigger value="executive" className="shrink-0 text-xs sm:text-sm">Summary</TabsTrigger>
              <TabsTrigger value="financials" className="shrink-0 text-xs sm:text-sm">Financials</TabsTrigger>
              <TabsTrigger value="inventory" className="shrink-0 text-xs sm:text-sm">Inventory</TabsTrigger>
              <TabsTrigger value="cashflow" className="shrink-0 text-xs sm:text-sm">Cash Flow</TabsTrigger>
            </TabsList>

            <TabsContent value="executive" className="space-y-4 min-w-0">
              <div className="grid lg:grid-cols-3 gap-3 sm:gap-4">
                <div className="lg:col-span-2 space-y-4">
                  <Card className="min-w-0 overflow-hidden">
                    <CardHeader className="min-w-0">
                      <CardTitle className="text-base sm:text-lg break-words">
                        {(data.periodMonths ?? 1) > 1 ? "Monthly Revenue Trend" : "Revenue vs Previous Month"}
                      </CardTitle>
                      <CardDescription className="break-words">
                        {(data.periodMonths ?? 1) > 1
                          ? "Revenue by month for the rolling period"
                          : "Daily revenue trend for selected month"}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="overflow-x-auto">
                      <ChartContainer
                        id="revenue-trend"
                        config={{ value: { label: "Revenue", color: "hsl(142, 76%, 36%)" } }}
                        className="h-[200px] sm:h-[260px] w-full min-w-[300px]"
                      >
                        <LineChart
                          data={data.charts.revenueSeries.map((r) => ({
                            ...r,
                            label: formatChartDate(r.date, data.periodMonths ?? 1),
                          }))}
                          margin={{ left: 48, right: 32, top: 16, bottom: 28 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                          <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                          <YAxis width={56} tick={{ fontSize: 12 }} tickFormatter={(v) => formatKAxis(v)} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
                          <RechartsTooltip formatter={(v: number) => [formatK(v), "Revenue"]} contentStyle={{ borderRadius: 8, fontSize: 13 }} />
                          <Line type="monotone" dataKey="value" stroke="hsl(142, 76%, 36%)" strokeWidth={3} dot={{ r: 5 }} activeDot={{ r: 7 }} />
                        </LineChart>
                      </ChartContainer>
                    </CardContent>
                  </Card>
                  <Card className="min-w-0 overflow-hidden">
                    <CardHeader className="min-w-0">
                      <CardTitle className="flex items-center gap-2 min-w-0 flex-wrap break-words text-base sm:text-lg">
                        Top 5 Most Profitable Products
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs">
                            <p className="font-medium mb-1">Sorting & metrics</p>
                            <p className="text-xs">Sorted by Gross Profit (Revenue − COGS). Margin = Net Profit ÷ Revenue. Profit per Unit = Gross Profit ÷ Units Sold.</p>
                          </TooltipContent>
                        </Tooltip>
                      </CardTitle>
                      <CardDescription className="break-words">Sorted by gross profit (revenue minus cost of goods sold)</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4 overflow-x-auto">
                      <ChartContainer
                        id="top5-gross-profit"
                        config={{ grossProfit: { label: "Gross Profit", color: "hsl(142, 76%, 36%)" } }}
                        className="h-[160px] sm:h-[200px] w-full min-w-[340px]"
                      >
                        <BarChart
                          data={data.executiveSummary.topProducts.map((p) => ({
                            name: p.name,
                            grossProfit: p.grossProfit ?? p.revenue,
                            unitsSold: p.unitsSold ?? 0,
                            revenue: p.revenue,
                            profitPerUnit: p.profitPerUnit ?? 0,
                          }))}
                          layout="vertical"
                          margin={{ left: 8, right: 24, top: 8, bottom: 8 }}
                        >
                          <defs>
                            <linearGradient id="barGradient" x1="0" y1="0" x2="1" y2="0">
                              <stop offset="0%" stopColor="hsl(142, 76%, 36%)" stopOpacity={0.9} />
                              <stop offset="100%" stopColor="hsl(142, 76%, 42%)" stopOpacity={1} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} opacity={0.6} />
                          <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => formatKAxis(v)} axisLine={false} tickLine={false} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} axisLine={false} tickLine={false} />
                          <RechartsTooltip
                            content={({ active, payload }) => {
                              if (!active || !payload?.[0]) return null;
                              const d = payload[0].payload as { name: string; grossProfit: number; unitsSold: number; revenue: number; profitPerUnit: number };
                              return (
                                <div className="rounded-lg border bg-background p-3 shadow-md text-xs">
                                  <p className="font-semibold mb-1">{d.name}</p>
                                  <p>Gross Profit: {formatK(d.grossProfit)}</p>
                                  <p>Revenue: {formatK(d.revenue)}</p>
                                  <p>Units Sold: {d.unitsSold}</p>
                                  <p>Profit/Unit: {formatK(d.profitPerUnit)}</p>
                                </div>
                              );
                            }}
                          />
                          <Bar dataKey="grossProfit" name="Gross Profit" radius={[0, 6, 6, 0]} fill="url(#barGradient)" maxBarSize={28} />
                        </BarChart>
                      </ChartContainer>
                      <div className="overflow-x-auto -mx-1 px-1 sm:mx-0 sm:px-0">
                        <table className="w-full text-sm min-w-[420px]" style={{ tableLayout: "auto" }}>
                          <thead>
                            <tr className="border-b-2 border-border">
                              <th className="text-left py-3 font-semibold">Product</th>
                              <th className="text-right py-3 font-semibold">Qty</th>
                              <th className="text-right py-3 font-semibold">Revenue</th>
                              <th className="text-right py-3 font-semibold">Gross Profit</th>
                              <th className="text-right py-3 font-semibold">Profit/Item</th>
                              {data.compareToPrevious && (
                                <th className="text-right py-3 font-semibold">Velocity</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {data.executiveSummary.topProducts.map((p, i) => (
                              <tr key={i} className="border-b border-border/50 last:border-0">
                                <td className="py-2 font-medium break-words min-w-0">{p.name}</td>
                                <td className="py-2 text-right tabular-nums">{p.unitsSold ?? 0}</td>
                                <td className="py-2 text-right tabular-nums">{formatK(p.revenue)}</td>
                                <td className="py-2 text-right tabular-nums">{formatK(p.grossProfit ?? p.revenue)}</td>
                                <td className="py-2 text-right tabular-nums">{formatK(p.profitPerUnit ?? 0)}</td>
                                {data.compareToPrevious && (
                                  <td className="py-2 text-right">
                                    {(p.momUnitsChangePct ?? 0) >= 0 ? (
                                      <ArrowUpRight className="h-4 w-4 inline text-emerald-600" />
                                    ) : (
                                      <ArrowDownRight className="h-4 w-4 inline text-red-600" />
                                    )}
                                    <span className={(p.momUnitsChangePct ?? 0) >= 0 ? "text-emerald-600" : "text-red-600"}>
                                      {formatPct(p.momUnitsChangePct ?? 0)}
                                    </span>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                <div className="min-w-0 lg:min-w-0">
                  <AIInsightPanel
                    insights={data.ai.insights}
                    priority={data.ai.priority}
                    focusArea={data.ai.focusArea}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="financials" className="space-y-4 min-w-0">
              <div className="grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <Card>
                    <CardHeader>
                      <CardTitle>Formal P&L</CardTitle>
                      <CardDescription>Revenue minus COGS, then operating expenses and estimated tax to net profit</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-0 font-mono text-sm">
                        <div className="flex justify-between py-2.5 border-b">
                          <span>Revenue</span>
                          <span>{formatK(data.financials.revenue)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b pl-4">
                          <span className="text-muted-foreground">Less: COGS</span>
                          <span className="text-red-600">-{formatK(data.financials.cogs)}</span>
                        </div>
                        <div className="flex justify-between py-2.5 border-b font-bold">
                          <span>= Gross Profit</span>
                          <span>{formatK(data.financials.grossProfit)}</span>
                        </div>
                        <div className="pt-2">
                          <div className="text-muted-foreground text-xs uppercase tracking-wider mb-1">Operating Expenses</div>
                          {data.financials.expensesByCategory && Object.keys(data.financials.expensesByCategory).length > 0 ? (
                            <>
                              {Object.entries(data.financials.expensesByCategory)
                                .sort(([a], [b]) => a.localeCompare(b))
                                .map(([cat, amt]) => (
                                  <div key={cat} className="flex justify-between py-1.5 pl-4 text-muted-foreground">
                                    <span>{cat}</span>
                                    <span className="text-red-600">-{formatK(amt)}</span>
                                  </div>
                                ))}
                            </>
                          ) : (
                            <div className="flex justify-between py-1.5 pl-4 text-muted-foreground">
                              <span>Total</span>
                              <span className="text-red-600">-{formatK(data.financials.operatingExpenses)}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex justify-between py-2 border-b font-bold">
                          <span>= Total OPEX</span>
                          <span className="text-red-600">-{formatK(data.financials.operatingExpenses)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b pl-4">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-muted-foreground cursor-help">Less: Tax (5% of Revenue, estimated)</span>
                            </TooltipTrigger>
                            <TooltipContent>Planning estimate applied after gross profit and operating expenses. Not a filed tax return.</TooltipContent>
                          </Tooltip>
                          <span className="text-red-600">-{formatK(data.financials.taxOnRevenue)}</span>
                        </div>
                        <div
                          className={`flex justify-between py-4 mt-2 rounded-lg px-3 ${
                            data.financials.netProfit >= 0 ? "bg-emerald-500/10 dark:bg-emerald-500/20" : "bg-red-500/10 dark:bg-red-500/20"
                          }`}
                        >
                          <span className="font-bold text-base">Net Profit</span>
                          <span className={`font-bold text-xl tabular-nums ${data.financials.netProfit >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}`}>
                            {formatK(data.financials.netProfit)}
                          </span>
                        </div>
                        <div className="flex justify-between py-2 text-muted-foreground text-xs">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help">Margin %</span>
                            </TooltipTrigger>
                            <TooltipContent>Net Profit ÷ Revenue. Percentage of revenue kept as profit after all costs.</TooltipContent>
                          </Tooltip>
                          <span>Margin {data.financials.marginPct.toFixed(1)}%</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                <div>
                  <AIInsightPanel
                    insights={data.ai.insights}
                    priority={data.ai.priority}
                    focusArea={data.ai.focusArea}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="inventory" className="space-y-4 min-w-0">
              <InventorySection data={data} formatK={formatK} />
            </TabsContent>

            <TabsContent value="cashflow" className="space-y-4 min-w-0">
              <CashFlowSection data={data} formatK={formatK} formatPct={formatPct} />
            </TabsContent>
          </Tabs>
        </>
      ) : (
        <Card>
          <CardContent className="py-16 text-center space-y-4">
            <p className="text-muted-foreground">
              Choose a period and end month, then click <strong>Load Report</strong> to generate the executive report.
            </p>
            <Button onClick={() => fetchReport()} disabled={loading || !user}>
              <Play className="h-4 w-4 mr-2" />
              {loading ? "Loading…" : "Load Report"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
